import { useCallback, useEffect, useRef, useState } from 'react'
import { api, session } from './api.js'
import Login from './pages/Login.jsx'
import TableauDeBord from './pages/TableauDeBord.jsx'
import Dossiers from './pages/Dossiers.jsx'
import Dossier from './pages/Dossier.jsx'
import RendezVous from './pages/RendezVous.jsx'
import Envois from './pages/Envois.jsx'
import Bibliotheque from './pages/Bibliotheque.jsx'
import Demarches from './pages/Demarches.jsx'
import Apparence from './pages/Apparence.jsx'
import Circuits from './pages/Circuits.jsx'
import Courriels from './pages/Courriels.jsx'
import Creneaux from './pages/Creneaux.jsx'
import Publication from './pages/Publication.jsx'
import Collectivites from './pages/Collectivites.jsx'
import Parametres from './pages/Parametres.jsx'
import Agents from './pages/Agents.jsx'
import Services from './pages/Services.jsx'
import Donnees from './pages/Donnees.jsx'
import Bilan from './pages/Bilan.jsx'
import MonCompte from './pages/MonCompte.jsx'

const CONFIG = 'config.modifier'
const AGENTS = 'agents.gerer'
const COLLECTIVITES = 'collectivites.gerer'

// Chaque entrée déclare le droit qu'elle exige : un agent d'accueil ne voit
// même pas les écrans qu'il ne pourrait pas utiliser.
const MENU = [
  { g: 'Traitement' },
  { id: 'tableau', label: 'Tableau de bord' },
  { id: 'dossiers', label: 'Dossiers' },
  { id: 'rendezvous', label: 'Rendez-vous' },
  { id: 'envois', label: 'Envois' },
  { id: 'bilan', label: 'Ce que ça donne' },
  { g: 'Mon portail', droit: CONFIG },
  { id: 'bibliotheque', label: 'Ajouter des démarches', droit: CONFIG },
  { id: 'demarches', label: 'Mes démarches', droit: CONFIG },
  { id: 'apparence', label: 'Apparence du portail', droit: CONFIG },
  { g: 'Réglages' },
  { id: 'circuits', label: 'Circuits de traitement', droit: CONFIG },
  { id: 'courriels', label: 'Courriels types', droit: CONFIG },
  { id: 'creneaux', label: 'Créneaux de rendez-vous', droit: CONFIG },
  { id: 'services', label: 'Services', droit: CONFIG },
  { id: 'agents', label: 'Agents et droits', droit: AGENTS },
  { id: 'donnees', label: 'Données personnelles', droit: CONFIG },
  { id: 'collectivites', label: 'Mes collectivités', droit: COLLECTIVITES },
  { id: 'parametres', label: 'Paramètres', droit: CONFIG },
  { id: 'moncompte', label: 'Mon compte' },
]

// Ces écrans modifient des définitions : ils ne s'ouvrent qu'en brouillon.
const EDITION = ['bibliotheque', 'apparence', 'circuits', 'courriels', 'creneaux', 'services']

export default function App() {
  const [moi, setMoi] = useState(null)
  const [droits, setDroits] = useState([])
  const [page, setPage] = useState({ id: 'tableau' })
  const [attente, setAttente] = useState(0)
  const [erreur, setErreur] = useState('')
  const [menuOuvert, setMenuOuvert] = useState(false)
  const [aide, setAide] = useState(false)
  // Le filtre et la recherche de la liste, retenus pour que « dossier suivant »
  // suive la même liste que celle d'où l'on vient.
  const [contexteListe, setContexteListe] = useState({ statut: 'open', q: '' })
  const droitsRef = useRef([])

  const charger = useCallback(async () => {
    try {
      const me = await api.me()
      setMoi(me)
      if (!session.org && me.orgs.length) session.setOrg(me.orgs[0].id)
      const profil = await api.profile().catch(() => null)
      setDroits(profil?.permissions || [])
      droitsRef.current = profil?.permissions || []
      if (profil?.permissions?.includes('config.publier')) {
        const p = await api.publishPreview()
        setAttente(p.changes.length)
      }
    } catch (e) { setErreur(e.message) }
  }, [])

  useEffect(() => { if (session.token) charger() }, [charger])

  // Raccourcis : ils font gagner du temps à qui passe la journée dedans, et
  // restent invisibles pour les autres. Jamais quand on est en train d'écrire.
  useEffect(() => {
    function touche(e) {
      if (e.target.matches('input, textarea, select') || e.metaKey || e.ctrlKey) return
      const vers = { d: 'dossiers', t: 'tableau', r: 'rendezvous', m: 'demarches' }[e.key]
      // « Mes démarches » demande le droit de configurer : sans lui, la touche
      // ne fait rien plutôt que d'ouvrir un écran en lecture seule déroutante.
      if (vers && (vers !== 'demarches' || droitsRef.current.includes('config.modifier'))) {
        setPage({ id: vers })
        setMenuOuvert(false)
      }
      if (e.key === '?') setAide((a) => !a)
      if (e.key === 'Escape') { setMenuOuvert(false); setAide(false) }
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  }, [])

  if (!session.token) return <Login onLogin={charger} />
  if (!moi) return <p className="chargement">{erreur || 'Chargement…'}</p>

  const org = moi.orgs.find((o) => o.id === session.org) || moi.orgs[0]
  const aller = (id, param) => { setPage({ id, param }); setMenuOuvert(false) }
  const ouvrirDossier = (id) => { setPage({ id: 'dossier', param: id }); setMenuOuvert(false) }
  const autorise = (droit) => !droit || droits.includes(droit)
  const visible = MENU.filter((m) => autorise(m.droit))

  return (
    <>
      <header className="console">
        <button className="burger" aria-label="Menu" aria-expanded={menuOuvert}
                onClick={() => setMenuOuvert(!menuOuvert)}>
          <span /><span /><span />
        </button>
        <span className="marque">UCIVIS</span>
        <select value={org.id} aria-label="Collectivité"
                onChange={(e) => { session.setOrg(e.target.value); window.location.reload() }}>
          {moi.orgs.map((o) => (
            <option key={o.id} value={o.id}>{o.name}{o.kind === 'agglo' ? ' (intercommunalité)' : ''}</option>
          ))}
        </select>
        <div className="env" role="group" aria-label="Environnement">
          <button className={session.env === 'draft' ? 'on' : ''}
                  onClick={() => { session.setEnv('draft'); window.location.reload() }}>Brouillon</button>
          <button className={session.env === 'live' ? 'on' : ''}
                  onClick={() => { session.setEnv('live'); window.location.reload() }}>En ligne</button>
        </div>
        {droits.includes('config.publier') && (attente > 0
          ? <button className="btn pri sm" onClick={() => aller('publication')}>
              {attente} changement{attente > 1 ? 's' : ''} à mettre en ligne
            </button>
          : <span className="ok">portail à jour</span>)}
        <div className="pousse" />
        <button className="lien" title="Raccourcis clavier (touche ?)"
                onClick={() => setAide(true)}>?</button>
        <button className="lien" onClick={() => { session.clear(); window.location.reload() }}>Quitter</button>
      </header>

      {aide && (
        <div className="fenetre" role="dialog" aria-label="Raccourcis clavier"
             onClick={() => setAide(false)}>
          <div className="fenetre-corps" onClick={(e) => e.stopPropagation()}>
            <h2>Raccourcis clavier</h2>
            <p className="aide">Ils fonctionnent partout, sauf quand vous êtes en train d'écrire
              dans un champ.</p>
            <dl className="raccourcis">
              <div><dt>T</dt><dd>Tableau de bord</dd></div>
              <div><dt>D</dt><dd>Dossiers</dd></div>
              {droits.includes('config.modifier') && (
                <div><dt>M</dt><dd>Mes démarches</dd></div>
              )}
              <div><dt>R</dt><dd>Rendez-vous</dd></div>
              <div><dt>J</dt><dd>Dossier suivant</dd></div>
              <div><dt>K</dt><dd>Dossier précédent</dd></div>
              <div><dt>Échap</dt><dd>Fermer, ou revenir à la liste</dd></div>
              <div><dt>?</dt><dd>Cette fenêtre</dd></div>
            </dl>
            <div className="ligne separee">
              <div className="pousse" />
              <button className="btn pri" onClick={() => setAide(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      <div className="app">
        {menuOuvert && <div className="voile" onClick={() => setMenuOuvert(false)} />}
        <nav className={`rail ${menuOuvert ? 'ouvert' : ''}`} aria-label="Sections">
          <div className="titre">{org.name}<span>{org.population.toLocaleString('fr-FR')} habitants</span></div>
          {visible.map((m, i) => m.g
            ? <p className="groupe" key={i}>{m.g}</p>
            : <button key={m.id} className={page.id === m.id ? 'on' : ''}
                      onClick={() => aller(m.id)}>{m.label}</button>)}
        </nav>

        <main>
          {attente > 0 && droits.includes('config.publier') && page.id !== 'publication' && (
            <div className="rappel-publication">
              <span>{attente} changement{attente > 1 ? 's' : ''} ne {attente > 1 ? 'sont' : 'est'} pas
                encore visible{attente > 1 ? 's' : ''} des habitants.</span>
              <button className="btn sm pri" onClick={() => aller('publication')}>
                Mettre en ligne
              </button>
            </div>
          )}
          {session.env === 'live' && EDITION.includes(page.id)
            ? <LectureSeule />
            : <>
                {page.id === 'tableau' && <TableauDeBord onOuvrir={ouvrirDossier} onAller={aller} droits={droits} />}
                {page.id === 'dossiers' && <Dossiers onOuvrir={ouvrirDossier}
                                                     onContexte={setContexteListe} />}
                {page.id === 'dossier' && <Dossier id={page.param} contexte={contexteListe}
                                                   onOuvrir={ouvrirDossier}
                                                   onRetour={() => aller('dossiers')} />}
                {page.id === 'rendezvous' && <RendezVous onOuvrir={ouvrirDossier} />}
                {page.id === 'envois' && <Envois onOuvrir={ouvrirDossier} />}
                {page.id === 'bibliotheque' && (
                  <Bibliotheque orgId={org.id}
                                onOuvrir={(key, onglet) => setPage({ id: 'demarches', param: { key, onglet } })} />
                )}
                {page.id === 'demarches' && <Demarches ouvrir={page.param} />}
                {page.id === 'apparence' && <Apparence />}
                {page.id === 'circuits' && <Circuits />}
                {page.id === 'courriels' && <Courriels />}
                {page.id === 'creneaux' && <Creneaux />}
                {page.id === 'publication' && <Publication onFini={charger} />}
                {page.id === 'collectivites' && <Collectivites moi={moi} droits={droits} />}
                {page.id === 'parametres' && <Parametres />}
                {page.id === 'agents' && <Agents />}
                {page.id === 'services' && <Services />}
                {page.id === 'donnees' && <Donnees />}
                {page.id === 'bilan' && <Bilan onOuvrir={ouvrirDossier} />}
                {page.id === 'moncompte' && <MonCompte />}
              </>}
        </main>
      </div>
    </>
  )
}

function LectureSeule() {
  return (
    <div className="carte vide">
      <h2>Ici, on regarde sans toucher</h2>
      <p>Cette version est celle que voient les habitants en ce moment. Les modifications se
        préparent dans le brouillon, puis se mettent en ligne quand vous êtes prêt.</p>
      <button className="btn pri" onClick={() => { session.setEnv('draft'); window.location.reload() }}>
        Passer au brouillon
      </button>
    </div>
  )
}
